/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import {
  useTrainModelMutation,
  useUpdateNoteMutation,
} from '../../common/apis/FlorenceApi';
import {
  SaveSheet,
  SheetStatus,
  UpdateSheetPayload,
} from '../../common/types/SaveSheet';
import SnackbarUtils from '../../common/utils/SnackbarUtilsConfigurator';

export const useNotes = (notes?: SaveSheet[]) => {
  const [trainModel] = useTrainModelMutation();
  const [updateNoteRequest] = useUpdateNoteMutation();

  const [page, setPage] = React.useState(0);

  const [updatedNotes, setUpdatedNotes] = React.useState<SaveSheet[]>(
    notes ?? []
  );

  const finalizeNote = async (
    finalizedNote: SaveSheet,
    showConfirmationMessage = true
  ) => {
    try {
      await trainModel(finalizedNote).unwrap();
      if (showConfirmationMessage) {
        SnackbarUtils.success('Op Note finalized successfully.');
      }
      const updatedNotes = notes?.map((n) => {
        if (n.sheetId === finalizedNote.sheetId) {
          return {
            ...n,
            scanText: finalizedNote.scanText,
            sheetCodes: finalizedNote.sheetCodes,
            clinicNoteResults: finalizedNote.clinicNoteResults,
            aiOutput: finalizedNote.aiOutput,
            status: SheetStatus.FINALIZED,
          };
        }
        return n;
      });
      setUpdatedNotes(updatedNotes ?? []);
    } catch (error: any) {
      if (error.message) {
        SnackbarUtils.error(error.message);
      }
    }
  };

  const updateNote = async (
    updatedNote: UpdateSheetPayload,
    showConfirmationMessage = false,
    updateLocally = true
  ) => {
    try {
      const noteToSend = {
        ...updatedNote,
        sheetCodes: updatedNote.sheetCodes.map((code) => {
          const newCode = { ...code };
          if (newCode.icd10) {
            newCode.icd10 = newCode.icd10?.filter((icd) => icd.selected);
          }

          return newCode;
        }),
      };

      if (updateLocally) {
        const updatedNotes = notes?.map((n) => {
          if (n.sheetId === updatedNote.sheetId) {
            return {
              ...n,
              scanText: updatedNote.scanText,
              sheetCodes: noteToSend.sheetCodes,
              clinicNoteResults: updatedNote.clinicNoteResults,
              aiOutput: updatedNote.aiOutput,
              status: updatedNote.status,
            };
          }
          return n;
        });
        setUpdatedNotes(updatedNotes ?? []);
      }

      await updateNoteRequest(noteToSend).unwrap();

      if (showConfirmationMessage) {
        SnackbarUtils.success(
          updatedNote.clinicNote
            ? 'Clinic Note saved successfully.'
            : 'Op Note saved successfully.'
        );
      }
    } catch (error: any) {
      if (error.message) {
        SnackbarUtils.error(error.message);
      }
    }
  };

  const changeClinicNoteStatus = async (
    note: SaveSheet,
    status: string,
    keepCodes = false
  ) => {
    await updateNote(
      {
        ...note,
        updateClinicNoteStatus: true,
        sheetCodes: keepCodes ? note.sheetCodes : [],
        clinicNoteResults: {
          status_response: status,

          final_code: '',
          code_details: null,

          cpt_response: '',
          icd_response: '',
          hcpc_response: '',

          data_response: '',
          problem_response: '',
          risk_response: '',
        },
        aiOutput: null,
      },
      false
    );
  };

  const updateNoteText = async (note: SaveSheet, text: string) => {
    setUpdatedNotes(
      updatedNotes.map((n) => {
        if (n.sheetId !== note.sheetId) {
          return n;
        }
        return {
          ...note,
          scanText: text,
        };
      })
    );
  };

  const updateNoteTextWithoutDeepCopy = async (
    note: SaveSheet,
    text: string
  ) => {
    setUpdatedNotes(
      updatedNotes.map((n) => {
        if (n.sheetId !== note.sheetId) {
          return n;
        }

        const aux = note;
        aux.scanText = text;

        return aux;
      })
    );
  };

  const refreshNote = async (newNote: SaveSheet) => {
    const newUpdatedNotes = updatedNotes?.map((oldNote) => {
      if (oldNote.sheetId === newNote.sheetId) {
        return {
          ...oldNote,
          scanText: newNote.scanText,
          sheetCodes: newNote.sheetCodes,
          clinicNoteResults: newNote.clinicNoteResults,
          aiOutput: newNote.aiOutput,
          status: newNote.status,
        };
      }
      return oldNote;
    });

    setUpdatedNotes(newUpdatedNotes ?? []);
  };

  return {
    currentNote: updatedNotes.length ? updatedNotes[page] : null,
    page,
    finalizeNote,
    updateNote,
    changeClinicNoteStatus,
    updateNoteText,
    updateNoteTextWithoutDeepCopy,
    setPage,
    refreshNote,
  };
};
